import { globalDef } from "../siteDefinitions";
import { onlyNumbers } from "../utility";
import { FundraiserValidation } from "../validation/FundraiserValidation";
import { handleAccountRequired } from "./accountFields";
import { initDatepicker } from "./datepicker";

// WYSIWYG
import suneditor from "suneditor";
// import plugins from "suneditor/src/plugins";
import { font, fontSize, formatBlock, horizontalRule, list, align, paragraphStyle, textStyle, link, blockquote, image } from "suneditor/src/plugins";

/**
 * Set up the fundraiser form.
 */
export class FundraiserForm {
    private fundraiserForm = document.querySelector(".fundraiser-form") as HTMLFormElement;
    private fundraiserValidation = new FundraiserValidation(this.fundraiserForm);
    private customImageWrapper = document.querySelector(".fundraiser-form__fieldset--profile-image") as HTMLElement;
    private profileImage: File | null = null;

    // Header image | fieldset and image
    private customImageWrapperHeader = document.querySelector(".fundraiser-form__fieldset--header-image") as HTMLElement;
    private Phot: File | null = null;

    constructor(private edit = false) {
        this.handleAccountRequire = this.handleAccountRequire.bind(this);

        this.setupAccountRequire();

        const dateInput = document.getElementById("End_Date") as HTMLInputElement | null;

        if (dateInput) {
            initDatepicker(dateInput);
        }

        if (this.customImageWrapper) {
            this.setupImageUpload();

            this.dropHandlers();

            this.removeImageHandler();
        }

        if (this.customImageWrapperHeader) {
            this.setupImageUploadHeader();

            // this.dropHandlersHeader();
        }

        this.setPreviewHeader();

        this.submitFundraiserHandler();

        handleAccountRequired();

        // this.loadEditor();

        this.generateWYSIWYG();

        document.querySelector(".inline-fend__fieldset")?.addEventListener("change", this.handleAccountRequire); // eslint-disable-line

        const accountRadios = document.querySelectorAll<HTMLElement>(".fundraiser-form__radio[name='account']");
        for (const accountRadio of accountRadios) {
            accountRadio.addEventListener("click", handleAccountRequired);
        }

        const goalInput = document.getElementById("Donation_Goal");
        if (goalInput) {
            goalInput.addEventListener("keypress", onlyNumbers);
        }

        // Initialize reCAPTCHA
        if (!globalDef.cmnDebug && document.getElementById("g-recaptcha")) {
            window.addEventListener("loadFundraiserRecaptcha", this.loadFundraiserRecaptcha);
        }
    }

    /**
     * Grab the form data and send it to the server.
     *
     * Shows validation if things are broken.
     */
    private async submitFundraiser() {
        const fundraiserData = new FormData(this.fundraiserForm);

        // Has to come after we've grabbed the data
        this.submitLoader(true);

        if (this.profileImage) {
            fundraiserData.append("Profile_Image", this.profileImage);
        }

        if (this.Phot) {
            fundraiserData.append("Header_Image", this.Phot);
        }

        if (!globalDef.cmnDebug) {
            // Add reCAPTCHA response
            fundraiserData.append("g_recaptcha_response", grecaptcha.getResponse());
        }

        const fundraiser = await this.sendFundraiserData(fundraiserData).catch((error: string) => new Error(error));

        // There was an error
        if (!fundraiser.fundraiserId) {
            this.submitLoader(false);
            return this.fundraiserValidation.manualValidation(fundraiser as any);
        }

        if (this.edit && fundraiser.status === 'approved') {
            location.href = `/fundraiser/${fundraiser.fundraiserId as string}/?fundraiser_created=true`;
        } else {
            this.showSuccessMessage();
        }
    }

    /**
     * Adds the submit event listener to the form.
     */
    private submitFundraiserHandler() {
        this.fundraiserForm.addEventListener("submit", (event) => {
            event.preventDefault();

            const valid = this.fundraiserValidation.validateAllFields();

            if (!valid) {
                return;
            }
            
            if (!globalDef.cmnDebug) {
                grecaptcha.execute();
            } else {
                this.submitFundraiser();
            }      
        });
    }

    /**
     * Load reCAPTCHA for fundraiser form
     */
    private loadFundraiserRecaptcha = () => {
        grecaptcha.render("g-recaptcha", {
            sitekey: "6LfoeqIUAAAAAJDliNy2Pfo5AnowXsGTOCpyj_Xp",
            callback: () => this.submitFundraiser(),
            size: "invisible",
        });
    };

    /**
     * Actually send the data to the server.
     * Handles the edit submission as well
     *
     * Returns a promise
     */
    private sendFundraiserData(formData: FormData) {
        let ajaxAction = "create-fundraiser";

        if (this.edit) {
            ajaxAction = "edit-fundraiser";
        }

        return new Promise<any>((resolve, reject) => {
            $.ajax({
                url: `${globalDef.ajaxFunctionsUrl}function=${ajaxAction}`,
                cache: false,
                method: "POST",
                type: "POST",
                processData: false,
                contentType: false,
                data: formData,
                success(response) {
                    resolve(JSON.parse(response));
                },
                error(error) {
                    reject(error);
                },
            });
        });
    }

    /**
     * Handles all the submitting loader logic
     */
    private submitLoader(submit = false) {
        const fieldsets = document.querySelectorAll<HTMLFieldSetElement>(".fundraiser-form__fieldset");

        if (submit) {
            this.fundraiserForm.classList.add("fundraiser-form--submitting");

            for (const fieldset of fieldsets) {
                fieldset.disabled = true;
            }

            (document.querySelector(".fundraiser-form__submit") as HTMLButtonElement).disabled = true;

            return;
        }

        this.fundraiserForm.classList.remove("fundraiser-form--submitting");

        for (const fieldset of fieldsets) {
            fieldset.disabled = false;
        }

        (document.querySelector(".fundraiser-form__submit") as HTMLButtonElement).disabled = false;
    }

    /**
     * The lazy way of adding require to the inline account form
     */
    private setupAccountRequire() {
        const createInputs = document.querySelectorAll<HTMLInputElement>(".inline-fend__act--create input");

        for (const createInput of createInputs) {
            createInput.required = true;
        }
    }

    private handleAccountRequire(e: Event) {
        const acctButton = e.target as HTMLInputElement;

        if (acctButton?.type !== "radio") {
            return;
        }

        const createInputs = document.querySelectorAll<HTMLInputElement>(".inline-fend__act--create input");
        const existingInputs = document.querySelectorAll<HTMLInputElement>(".inline-fend__act--existing input");

        if (acctButton.value === "create") {
            for (const createInput of createInputs) {
                createInput.required = true;
            }

            for (const existingInput of existingInputs) {
                existingInput.required = false;
            }
        }

        if (acctButton.value === "existing") {
            for (const createInput of createInputs) {
                createInput.required = false;
            }

            for (const existingInput of existingInputs) {
                existingInput.required = true;
            }
        }
    }

    /**
     * Load the WYSIWYG editor
     */
    // private loadEditor() {
    // eslint-disable-next-line @typescript-eslint/naming-convention,no-underscore-dangle,id-blacklist,id-match
    //     import(/* webpackChunkName: "quill-js" */ "quill").then(({ default: Quill }) => {
    //         const toolbarOptions = [[{ header: [false, 1, 2, 3] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], [{ align: [] }], ["video"], ["clean"]];

    //         const quill = new Quill(".fundraiser-form__text-editor", {
    //             theme: "snow",
    //             modules: {
    //                 toolbar: toolbarOptions,
    //             },
    //         });

    //         document.querySelector(".js-test")?.addEventListener("click", () => {
    //             const html = quill.root.innerHTML;

    //             console.log(html);
    //             console.log(quill.getContents());
    //             quill.root.innerHTML = `<p>fewa feaw eaw w</p><p><strong>efwafewaf w e<em><u>ewafjawoef</u></em></strong></p><p><br></p><p><br></p><p><br></p><p><strong><em><u>eufhawiufh awf waf </u></em></strong><em><u>fa</u></em></p><p><br></p><p><em><u>faweuifhweaiufhw aaw waj o</u></em></p><p><br></p><p class="ql-align-center">f weaf ewaf awef wa wa </p><p class="ql-align-center"><br></p><p class="ql-align-center"><br></p><p class="ql-align-right">fweaf awe fawe wae wa wae wae </p><p class="ql-align-right"><br></p><p class="ql-align-right">fweafawefwaef waf</p><p class="ql-align-right"><br></p><p>ewafaew fwaf wa</p>`;
    //         });
    //     });
    // }

    /**
     * Set the header image for the preview
     */
    private setPreviewHeader() {
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;
        const photoPreview = document.querySelector(".js-image-preview-header") as HTMLImageElement;
        const uploadBanner = document.querySelector(".upload_banner") as HTMLElement;

        // Support both CDN URLs (2023 and 2026)
        const stockImagesPath2026 = "https://resources.childrensmiraclenetworkhospitals.org/cdn/images/CorporateWebsite/2026/Campaign/stock-campaign-images";
        const stockImagesPaths = [stockImagesPath2026];
        const uploadIconURL = "https://resources.childrensmiraclenetworkhospitals.org/cdn/images/CorporateWebsite/ThemeImages/icon-upload.png";
        const fallbackImage = `${stockImagesPath2026}/cmn-fundraiser-banners-13.jpg`;

        // Helper function to check if URL is from stock images CDN
        const isStockImage = (url: string): boolean => {
            return stockImagesPaths.some(path => url.startsWith(path)) || url === uploadIconURL;
        };

        photoPreview.addEventListener("error", () => {
            photoPreview.src = fallbackImage;
            headerPreview.fundraiserImage = fallbackImage;
        });

        if (isStockImage(photoPreview.src)) {
            const checkedRadio = document.querySelector(".fundraiser-form__image-picker[name='Photo']:checked") as HTMLInputElement;
            if (checkedRadio && checkedRadio.value) {
                headerPreview.fundraiserImage = checkedRadio.value;
            } else {
                headerPreview.fundraiserImage = fallbackImage;
                const firstRadio = document.querySelector(".fundraiser-form__image-picker[name='Photo']") as HTMLInputElement;
                if (firstRadio && firstRadio.type === 'radio') {
                    firstRadio.checked = true;
                }
            }
            photoPreview.src = uploadIconURL;
        } else {
            headerPreview.fundraiserImage = photoPreview.src;
        }

        document.querySelector(".fundraiser-form__fieldset--selected-image")?.addEventListener("change", (event: Event) => {
            if ((event.target as HTMLInputElement).id !== "Photo") {
                headerPreview.fundraiserImage = (event.target as HTMLInputElement).value;
                (document.querySelector("#Photo") as HTMLInputElement).value = ""; // Clean file input
                uploadBanner.classList.remove("selected");
                document.querySelector("#Photo-error")?.classList.remove('error-message--active');
            }
        });
    }

    /**
     * Setup all the basic image upload stuff
     */
    private setupImageUpload() {
        this.customImageWrapper!.classList.add("fundraiser-form__image-upload--js");

        const uploadInput = document.querySelector(".fundraiser-form__image-upload") as HTMLInputElement;

        uploadInput.addEventListener("change", () => {
            const files = uploadInput.files;

            if (files && files.length > 0) {
                this.handleUploadImage(files[0]);
            }
        });

        (document.querySelector(".fundraiser-form__text-input--fund-name") as HTMLElement).addEventListener("blur", (event) => this.updatePreviewContent(event, "fundraiserName"));
        (document.querySelector(".fundraiser-form__text-input--headline") as HTMLElement).addEventListener("blur", (event) => this.updatePreviewContent(event, "fundraiserHeadline"));
    }

    /**
     * Check the uploaded image and make sure it's valid
     */
    private handleUploadImage(file: File) {
        const uploadInput = document.querySelector(".fundraiser-form__image-upload") as HTMLInputElement;
        const imageValid = this.fundraiserValidation.checkImage(file, uploadInput);

        if (imageValid) {
            this.profileImage = file;
            this.showUploadPreview(file);

            this.customImageWrapper.classList.add("image-uploaded");

            if (this.edit) {
                (document.querySelector("input[name='deleted_image']") as HTMLInputElement).checked = false;
            }
        } else {
            this.removeUploadPreview();

            this.customImageWrapper.classList.remove("image-uploaded");
        }
    }

    /**
     * Show the image in the preview area
     */
    private showUploadPreview(file: File) {
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;

        // The preview in the text section
        const textImagePreview = document.querySelector(".js-image-preview") as HTMLImageElement;

        // Check that files exists in the validation class
        const blobUrl = URL.createObjectURL(file);

        // The component handles revoking the url
        headerPreview.profileImage = blobUrl;
        textImagePreview.src = blobUrl;
    }

    /**
     * Hide the image if one has already been uploaded.
     * Basically return to the default
     */
    private removeUploadPreview() {
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;

        headerPreview.profileImage = "";
    }

    /**
     * Update the image preview content with the actual name and such
     */
    private updatePreviewContent(event: Event, previewSection: string) {
        const { value } = event.currentTarget as HTMLInputElement;
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;

        if (headerPreview && value !== "") {
            headerPreview[previewSection] = value;
        }
    }

    /**
     * Send the dropped image off to the image handler
     */
    private handleDrop(e: DragEvent) {
        const dt = e.dataTransfer;

        if (dt) {
            const file = dt.files;

            this.handleUploadImage(file[0]);
        }
    }

    /**
     * Set up the cancel button for uploaded profile image
     *
     * This will remove the image from the preview, hide the upload text, and remove the image from form data
     */
    private removeImageHandler() {
        const removeButton = document.querySelector(".fundraiser-form__image-remove-upload");
        const uploadInput = document.querySelector(".fundraiser-form__image-upload") as HTMLInputElement;

        if (!removeButton || !uploadInput) {
            return;
        }

        removeButton.addEventListener("click", () => {
            this.profileImage = null;
            this.removeUploadPreview();

            this.customImageWrapper.classList.remove("image-uploaded");
            uploadInput.value = "";

            if (this.edit) {
                (document.querySelector("input[name='deleted_image']") as HTMLInputElement).checked = true;
            }
        });
    }

    /**
     * Handlers for the drag and drop interface
     */
    private dropHandlers() {
        ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
            this.customImageWrapper!.addEventListener(
                eventName,
                (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                },
                false
            );
        });

        this.customImageWrapper.addEventListener("dragenter", () => this.customImageWrapper.classList.add("fundraiser-form__fieldset--highlight"), false);

        this.customImageWrapper.addEventListener(
            "dragover",
            (event: DragEvent) => {
                if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = "copy";
                }
            },
            false
        );

        ["dragleave", "drop"].forEach((eventName) => {
            this.customImageWrapper.addEventListener(eventName, () => this.customImageWrapper.classList.remove("fundraiser-form__fieldset--highlight"), false);
        });

        this.customImageWrapper.addEventListener("drop", (event: DragEvent) => this.handleDrop(event), false);
    }

    /**
     * ### Adding WYSIWYG ###
     */

    private generateWYSIWYG() {
        const editor = suneditor.create("Mission_Statement", {
            plugins: [font, fontSize, formatBlock, horizontalRule, list, align, paragraphStyle, textStyle, link, blockquote, image],
            buttonList: [
                ["undo", "redo"],
                ["fontSize", "formatBlock"],
                ["bold", "underline", "italic", "list", "blockquote", "removeFormat"],
                ["align", "outdent", "indent", "horizontalRule"],
                ["link", "image"],
                ["fullScreen"], // showBlocks, codeView, table, fontColor
                // '/', // Line break
            ],
            maxCharCount: 2000,
            // imageUploadUrl: "/cdn/images/CorporateWebsite",
        });

        editor.setDefaultStyle("font-size: 20px; height: 500px");

        editor.onChange = function (contents: string) {
            (document.getElementById("Mission_Statement") as HTMLTextAreaElement).value = contents;
        };

        editor.onImageUploadBefore = function (files: Array<File>, _info: string, _core: () => void, uploadHandler: (result?: ImageHandler) => void) {
            //Prepare form data
            const formData = new FormData();
            formData.append("image", files[0]);

            $.ajax({
                url: `${globalDef.ajaxFunctionsUrl}function=submit-wysiwyg-image`,
                cache: false,
                type: "POST",
                processData: false,
                contentType: false,
                data: formData,
                success(response) {
                    const src: string = JSON.parse(response).fallback.src;

                    const result = {
                        errorMessage: "Error saving image",
                        result: [
                            {
                                url: `${src}`,
                                name: files[0].name,
                                size: files[0].size,
                            },
                        ],
                    };

                    uploadHandler(result);
                },
                error() {
                    uploadHandler();
                },
            });
        };

        (document.getElementById("suneditor_Mission_Statement") as HTMLElement).setAttribute("style", "width: 100%");
    }
    /**
     * ### Image upload for Header image ###
     */

    /**
     * Setup all the basic image upload stuff
     */
    private setupImageUploadHeader() {
        this.customImageWrapperHeader!.classList.add("fundraiser-form__image-upload--js");

        const uploadInput = document.querySelector(".fundraiser-form__image-upload-header") as HTMLInputElement;

        const uploadContainer = document.querySelector(".arrow-container") as HTMLInputElement;

        uploadInput.addEventListener("change", () => {
            const files = uploadInput.files;

            if (files && files.length > 0) {
                this.handleUploadImageHeader(files[0]);
                uploadContainer.classList.add("image-uploaded");
            }
        });

        // (document.querySelector(".fundraiser-form__text-input--fund-name") as HTMLElement).addEventListener("blur", (event) => this.updatePreviewContent(event, "fundraiserName"));
        // (document.querySelector(".fundraiser-form__text-input--headline") as HTMLElement).addEventListener("blur", (event) => this.updatePreviewContent(event, "fundraiserHeadline"));
    }

    /**
     * Check the uploaded image and make sure it's valid
     */
    private handleUploadImageHeader(file: File) {
        const uploadInput = document.querySelector(".fundraiser-form__image-upload-header") as HTMLInputElement;
        const imageValid = this.fundraiserValidation.checkImage(file, uploadInput);
  
        if (imageValid) {
            this.Phot = file;
            this.showUploadPreviewHeader(file);
            
            document.querySelectorAll('.fundraiser-form__image-picker').forEach(function(e : HTMLInputElement) {
                e.checked = false;
            });
            document.querySelector(".upload_banner")?.classList.add("selected");
        } else {
            this.removeUploadPreviewHeader();
            document.querySelector(".upload_banner")?.classList.remove("selected");
        }
    }

    /**
     * Show the image in the preview area
     */
    private showUploadPreviewHeader(file: File) {
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;

        // The preview in the text section
        const textImagePreview = document.querySelector(".js-image-preview-header") as HTMLImageElement;

        // Check that files exists in the validation class
        const blobUrl = URL.createObjectURL(file);

        // The component handles revoking the url
        headerPreview.fundraiserImage = blobUrl;
        textImagePreview.src = blobUrl;
    }

    /**
     * Hide the image if one has already been uploaded.
     * Basically return to the default
     */
    private removeUploadPreviewHeader() {
        const headerPreview = document.querySelector(".fundraiser-form__preview") as any;

        headerPreview.fundraiserImage = "";
    }

    /**
     * Send the dropped image off to the image handler
     */
    // private handleDropHeader(e: DragEvent) {
    //     const dt = e.dataTransfer;

    //     if (dt) {
    //         const file = dt.files;
    //         document.querySelectorAll('.fundraiser-form__image-picker').forEach(function(e) {
    //             e.removeAttribute("checked");
    //         });

    //         this.handleUploadImageHeader(file[0]);
    //     }
    // }

    /**
     * Handlers for the drag and drop interface
     */
    // private dropHandlersHeader() {
    //     ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
    //         this.customImageWrapperHeader!.addEventListener(
    //             eventName,
    //             (e: Event) => {
    //                 e.preventDefault();
    //                 e.stopPropagation();
    //             },
    //             false
    //         );
    //     });

    //     this.customImageWrapperHeader.addEventListener("dragenter", () => this.customImageWrapperHeader.classList.add("fundraiser-form__fieldset--highlight"), false);

    //     this.customImageWrapperHeader.addEventListener(
    //         "dragover",
    //         (event: DragEvent) => {
    //             if (event.dataTransfer) {
    //                 event.dataTransfer.dropEffect = "copy";
    //             }
    //         },
    //         false
    //     );

    //     ["dragleave", "drop"].forEach((eventName) => {
    //         this.customImageWrapperHeader.addEventListener(eventName, () => this.customImageWrapperHeader.classList.remove("fundraiser-form__fieldset--highlight"), false);
    //     });

    //     this.customImageWrapperHeader.addEventListener("drop", (event: DragEvent) => {
    //         this.handleDropHeader(event), true});

    // }

    /**
     * Show success message after fundraiser submission
     */
    private showSuccessMessage() {
        this.fundraiserForm.style.display = 'none';
        
        const successMessage = document.querySelector('.fundraiser-success-message') as HTMLElement;
        if (successMessage) {
            successMessage.style.display = 'block';
        }
        
        // Remove the loading state
        this.submitLoader(false);
    }
}

interface ImageHandler {
    errorMessage: string;
    result: ImageHandlerResult[];
}

interface ImageHandlerResult {
    url: string;
    name: string;
    size: number;
}
